import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function BeginnerGuidePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-[72px]">
        {/* Article Hero */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[900px] mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/hipobuy-spreadsheet-guide" className="hover:text-primary-600 transition-colors cursor-pointer">Guide</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">Beginner Guide</span>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 border border-accent-200/60 mb-6">
              <i className="ri-seedling-line text-sm text-accent-600"></i>
              <span className="text-xs text-accent-700 font-medium">Getting Started</span>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight mb-6">
              Hipobuy Spreadsheet Beginner Guide: Start Your Research Journey in 2026
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-[700px] mb-8">
              A step-by-step walkthrough designed specifically for first-time users. Learn how to navigate
              spreadsheet-based shopping, understand category structures, apply filters like a pro, and
              avoid the most common mistakes beginners make when starting out.
            </p>

            <div className="flex flex-wrap items-center gap-6 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm"></i>
                Updated July 2026
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm"></i>
                13 min read
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line text-sm"></i>
                Beginner Support Team
              </span>
            </div>
          </div>
        </section>

        {/* Banner Image */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Welcoming%20and%20approachable%20beginner%20workspace%20scene%20with%20a%20notebook%20open%20next%20to%20a%20laptop%2C%20colorful%20sticky%20notes%2C%20a%20warm%20cup%20of%20coffee%2C%20soft%20morning%20light%20from%20a%20nearby%20window%2C%20clean%20beige%20desk%20surface%2C%20friendly%20and%20inviting%20atmosphere%2C%20educational%20tutorial%20concept%2C%20organized%20with%20gentle%20handwritten%20touches%2C%20warm%20neutral%20tones%2C%20cozy%20editorial%20photography&width=1800&height=800&seq=beginner-hero-banner-v2&orientation=landscape"
            alt="Hipobuy Spreadsheet beginner tutorial guide"
            title="Hipobuy Spreadsheet - Beginner Guide"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/60 via-transparent to-background-50/60"></div>
        </section>

        {/* Article Content */}
        <article className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[800px] mx-auto px-6 md:px-10">
            <div className="prose-custom space-y-12">
              {/* Section 1: Welcome */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Welcome to Spreadsheet Shopping — You Are in the Right Place
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  If you have never used a spreadsheet-based shopping tool before, the concept can feel a bit unfamiliar at first. But here is the truth: <strong>thousands of people just like you started exactly where you are right now, and within one session, they were navigating the Hipobuy Spreadsheet like seasoned pros.</strong> This guide is built specifically for complete beginners — no prior experience required, no technical jargon, just clear, actionable steps.
                </p>
                <p className="text-foreground-600 leading-relaxed text-base mt-4">
                  Think of the spreadsheet as your personal shopping assistant. Instead of wandering through endless marketplace pages hoping to stumble upon something good, the spreadsheet gives you a curated, organized view of products that have already been checked by the community. You get to see pricing, quality ratings, size information, and direct links — all in one place.
                </p>
                <p className="text-foreground-600 leading-relaxed text-base mt-4">
                  By the time you finish reading this guide (about 13 minutes), you will know exactly how to find products, compare options, and make your first purchase using the spreadsheet. Ready? Let us begin.
                </p>
              </section>

              {/* Section 2: Getting Started */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Step 1: Understanding the Spreadsheet Layout
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  Before you dive into browsing products, take a moment to understand how the spreadsheet is organized. Every Hipobuy Spreadsheet entry follows a consistent structure with standardized columns. Knowing what each column represents makes navigation intuitive from the start.
                </p>

                <div className="mt-6 bg-background-100 rounded-2xl p-6 md:p-8">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">Standard Spreadsheet Columns Explained</h3>
                  <div className="space-y-4">
                    {[
                      { col: 'Product Name', meaning: 'The name or title of the product as it appears on the source platform. Use this to identify items quickly.' },
                      { col: 'Price Range', meaning: 'The approximate price in your preferred currency. Prices are estimates and should be verified on the destination page before purchasing.' },
                      { col: 'Seller Rating', meaning: 'A rating from 1 to 5 stars based on community feedback and the seller\'s history. Prioritize sellers rated 4.5 stars and above.' },
                      { col: 'QC Status', meaning: 'Indicates whether the product has been verified by the community. Look for "Verified" or "QC Passed" badges for the most reliable listings.' },
                      { col: 'Size Guide', meaning: 'Size availability and fit notes. Pay close attention to this column, especially for clothing and footwear categories.' },
                      { col: 'Direct Link', meaning: 'The clickable link that takes you directly to the product page on the main platform. This is your gateway to making a purchase.' },
                    ].map((item) => (
                      <div key={item.col} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 bg-background-50 rounded-xl p-4 border border-background-200/40">
                        <span className="flex-shrink-0 px-3 py-1 rounded-lg bg-primary-100 text-primary-700 font-semibold text-xs whitespace-nowrap w-fit">{item.col}</span>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.meaning}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Section 3: First Browse */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Step 2: Your First Category Browse
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  Now that you understand the layout, it is time to browse your first category. We recommend starting with a popular category like <strong>Shoes</strong> or <strong>T-Shirts</strong> — these categories have the most listings, highest QC verification rates, and the most active community contributions. Starting with a well-maintained category builds confidence before exploring smaller ones.
                </p>

                <div className="mt-6 space-y-4">
                  {[
                    { num: '1', title: 'Pick a category', desc: 'Use the Categories dropdown in the navigation bar or the category cards on the homepage. Clicking a category takes you to the corresponding section on the main platform with the spreadsheet data pre-loaded for that product type.' },
                    { num: '2', title: 'Scan the listings', desc: 'Take 2-3 minutes to scroll through the entries without clicking anything. Notice how products are organized. Look at the price ranges, seller ratings, and QC badges to get a feel for what is available.' },
                    { num: '3', title: 'Apply mental filters', desc: 'As you scan, mentally note which products catch your eye. Are you looking for something under a certain price? Do you only want QC-verified items? This mental filtering prepares you for more targeted browsing later.' },
                    { num: '4', title: 'Open a few links', desc: 'Click on 3-5 product links that interest you. Each link opens the actual product page on the main platform in a new tab. This lets you verify that the spreadsheet data matches what you see on the product page.' },
                    { num: '5', title: 'Compare your findings', desc: 'Switch between your open tabs and compare the products side by side. Check pricing accuracy, review recency, and product image quality. This comparison step is where spreadsheet shopping really shines.' },
                  ].map((step) => (
                    <div key={step.num} className="flex gap-4 bg-background-100 rounded-xl p-5">
                      <span className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-bold text-sm">{step.num}</span>
                      <div>
                        <h4 className="font-semibold text-foreground-900 text-sm mb-1">{step.title}</h4>
                        <p className="text-foreground-600 text-sm leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 4: Common Mistakes */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  5 Common Beginner Mistakes (And How to Avoid Them)
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  Learning from other people's mistakes saves you time and frustration. Here are the five most common errors that new Hipobuy Spreadsheet users make — and exactly how to avoid each one.
                </p>

                <div className="mt-6 space-y-4">
                  {[
                    {
                      mistake: 'Trying to browse all 14 categories at once',
                      fix: 'Start with one category. Master it. Then expand to others. The spreadsheet is not going anywhere, and spreading your attention across too many categories at once leads to overwhelm and decision fatigue. Pick Shoes or T-Shirts, spend a full session exploring it, and only then move to your next category.',
                    },
                    {
                      mistake: 'Skipping the QC verification column',
                      fix: 'The QC Status column is arguably the most important column in the entire spreadsheet. It tells you whether community members have verified the product data. Unverified listings are not necessarily bad, but they carry more risk. For your first few purchases, stick to QC-verified entries only.',
                    },
                    {
                      mistake: 'Not cross-checking prices on the destination page',
                      fix: 'Prices in the spreadsheet are approximate and can change. Always check the actual price on the destination page before purchasing. If the price is significantly different from what the spreadsheet shows, the listing may be outdated or the spreadsheet entry may need updating.',
                    },
                    {
                      mistake: 'Ignoring seller ratings',
                      fix: 'Seller ratings are your best indicator of reliability. A 4.8-star seller with 200 reviews is almost always a safer bet than a 5.0-star seller with only 3 reviews. Look for sellers with consistent, long histories and recent positive feedback that mentions specific product details.',
                    },
                    {
                      mistake: 'Forgetting to save or bookmark good finds',
                      fix: 'When you find a product you like, save the link immediately. Open a notes app, a browser bookmark folder, or even a simple text file. Products can sell out, links can change, and you do not want to lose a great find because you assumed you would remember where it was. Many experienced users maintain a personal spreadsheet tracking their discoveries.',
                    },
                  ].map((item) => (
                    <div key={item.mistake} className="bg-accent-50 rounded-2xl p-6 border border-accent-200/50">
                      <div className="flex items-start gap-3 mb-2">
                        <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-200 text-accent-700 flex-shrink-0">
                          <i className="ri-error-warning-line text-sm"></i>
                        </span>
                        <h4 className="font-semibold text-foreground-950 text-base">{item.mistake}</h4>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed ml-10">
                        <strong className="text-accent-700">How to avoid it:</strong> {item.fix}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 5: Advanced Tips */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Leveling Up: Tips for Intermediate Users
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  Once you are comfortable with the basics, these intermediate tips help you get even more value from the Hipobuy Spreadsheet. These strategies come from power users who have been spreadsheet shopping for over a year.
                </p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { title: 'Create a Personal Wishlist Spreadsheet', desc: 'Copy the structure of the Hipobuy Spreadsheet into your own document. Add columns for personal notes, purchase dates, and satisfaction ratings. Over time, this becomes your personalized research database.' },
                    { title: 'Join Community Discussions', desc: 'The Hipobuy Spreadsheet community is active with daily discussions about new finds, quality issues, and category updates. Participating in these conversations gives you early access to recommendations and helps you build connections with experienced shoppers.' },
                    { title: 'Cross-Reference Multiple Categories', desc: 'Sometimes the best finds live in unexpected categories. A jacket you love might be listed under Sets. A bag might appear in Accessories or Bags. Browsing adjacent categories often surfaces hidden gems.' },
                    { title: 'Track Price Trends Over Time', desc: 'If you visit the same categories regularly, you will start noticing price patterns. Some products fluctuate seasonally. Others drop after initial hype. Tracking these trends helps you time your purchases for the best deals.' },
                    { title: 'Contribute Back to the Community', desc: 'Once you have made a few successful purchases, consider contributing to the spreadsheet. Submit new product entries, verify existing listings, or share your QC findings. Community contributions are what keep the data fresh and accurate for everyone.' },
                    { title: 'Combine with Traditional Research', desc: 'Use the spreadsheet as your starting point, not your only tool. After identifying promising products, search for reviews on external platforms, check social media for real-world photos, and read discussion threads for unfiltered opinions.' },
                  ].map((tip) => (
                    <div key={tip.title} className="bg-background-100 rounded-2xl p-5">
                      <h4 className="font-heading font-semibold text-foreground-950 text-base mb-2">{tip.title}</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 6: Learning Path */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Your Learning Path: What to Read Next
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  Congratulations — you now have everything you need to start using Hipobuy Spreadsheet confidently. Here is a recommended reading path to continue building your knowledge.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    {
                      order: '1st',
                      title: 'Complete Guide',
                      desc: 'Deep dive into every spreadsheet feature with detailed explanations.',
                      link: '/hipobuy-spreadsheet-guide',
                      icon: 'ri-book-open-line',
                    },
                    {
                      order: '2nd',
                      title: 'Safety Guide',
                      desc: 'Learn how to protect yourself and shop securely with confidence.',
                      link: '/spreadsheet-safety-guide',
                      icon: 'ri-shield-check-line',
                    },
                    {
                      order: '3rd',
                      title: 'Platform Review',
                      desc: 'See our honest, independent assessment of Hipobuy Spreadsheet.',
                      link: '/hipobuy-spreadsheet-review',
                      icon: 'ri-star-line',
                    },
                  ].map((item) => (
                    <Link
                      key={item.order}
                      to={item.link}
                      className="bg-background-100 rounded-2xl p-5 cursor-pointer hover:bg-background-200/50 transition-colors duration-200 group"
                    >
                      <span className="text-xs font-semibold text-accent-600 uppercase tracking-wider">{item.order}</span>
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 my-3 group-hover:bg-accent-200 transition-colors duration-200">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <h4 className="font-heading font-semibold text-foreground-950 text-base mb-1.5">{item.title}</h4>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </Link>
                  ))}
                </div>
              </section>

              {/* FAQ */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Beginner Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {[
                    {
                      q: 'Do I need any special software to use Hipobuy Spreadsheet?',
                      a: 'No special software is required. The spreadsheet data is accessible through any modern web browser on desktop or mobile. You do not need Excel, Google Sheets, or any spreadsheet application installed — simply visit the main platform and browse the organized product listings directly.',
                    },
                    {
                      q: 'How long does it take to learn spreadsheet shopping?',
                      a: 'Most beginners feel comfortable within their first 30-minute session. The learning curve is gentle because the spreadsheet uses intuitive column headers and consistent formatting across all categories. After 2-3 browsing sessions, navigating and comparing products becomes second nature.',
                    },
                    {
                      q: 'What if I cannot find a specific product I am looking for?',
                      a: 'The spreadsheet covers a wide range of products, but it does not contain every item available on the main platform. If you cannot find a specific product, try searching within adjacent categories, checking the main platform directly, or asking the community for recommendations. New products are added daily.',
                    },
                    {
                      q: 'Is it okay to buy from sellers with no QC badge?',
                      a: (
                        <>
                          For your first few purchases, we strongly recommend sticking to QC-verified listings. Once you are more experienced and comfortable evaluating sellers independently, you can cautiously explore unverified listings. Always apply the safety checklist regardless of QC status. Check our{' '}
                          <Link to="/spreadsheet-safety-guide" className="text-primary-600 underline underline-offset-2 hover:text-primary-700 transition-colors cursor-pointer">
                            safety guide
                          </Link>{' '}
                          for detailed protection strategies.
                        </>
                      ),
                    },
                  ].map((faq, i) => (
                    <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                      <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-200/50 transition-colors duration-150">
                        <h4 className="font-heading font-semibold text-foreground-950 text-base pr-4">{faq.q}</h4>
                        <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-open:rotate-45 transition-transform duration-200">
                          <i className="ri-add-line text-sm"></i>
                        </span>
                      </summary>
                      <div className="px-5 pb-5">
                        <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* CTA */}
              <section className="bg-primary-50 rounded-2xl p-8 md:p-10 text-center border border-primary-200/50">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  You Are Ready — Start Exploring Now
                </h2>
                <p className="text-foreground-600 text-sm max-w-lg mx-auto mb-6">
                  Apply everything you have learned and browse organized product listings across 14 categories on the main platform. Your first great find is just a few clicks away.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
                  >
                    Start Browsing Now
                    <i className="ri-arrow-right-up-line ml-1.5"></i>
                  </a>
                  <Link
                    to="/hipobuy-spreadsheet-guide"
                    className="px-7 py-3 rounded-full border-2 border-primary-500 text-primary-700 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-50 transition-colors duration-200"
                  >
                    Full Guide
                    <i className="ri-arrow-right-line ml-1.5"></i>
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      {/* BlogPosting Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            '@id': 'https://hipobuyspread.click/hipobuy-spreadsheet-beginner-guide/#article',
            'headline': 'Hipobuy Spreadsheet Beginner Guide: Start Your Research Journey in 2026',
            'description': 'A step-by-step walkthrough designed for first-time users. Learn to navigate spreadsheet-based shopping, understand categories, apply filters, and avoid common beginner mistakes.',
            'author': {
              '@type': 'Organization',
              'name': 'Hipobuy Spread',
              'url': 'https://hipobuyspread.click',
            },
            'publisher': {
              '@id': 'https://hipobuyspread.click/#organization',
            },
            'datePublished': '2026-07-01',
            'dateModified': '2026-07-12',
            'mainEntityOfPage': {
              '@type': 'WebPage',
              '@id': 'https://hipobuyspread.click/hipobuy-spreadsheet-beginner-guide',
            },
            'isPartOf': {
              '@id': 'https://hipobuyspread.click/#website',
            },
          }),
        }}
      />

      {/* FAQ Schema structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            '@id': 'https://hipobuyspread.click/hipobuy-spreadsheet-beginner-guide/#faq',
            'mainEntity': [
              {
                '@type': 'Question',
                'name': 'Do I need any special software to use Hipobuy Spreadsheet?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': 'No special software is required. The spreadsheet data is accessible through any modern web browser on desktop or mobile.',
                },
              },
              {
                '@type': 'Question',
                'name': 'How long does it take to learn spreadsheet shopping?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': 'Most beginners feel comfortable within their first 30-minute session. After 2-3 browsing sessions, navigating becomes second nature.',
                },
              },
              {
                '@type': 'Question',
                'name': 'Is it okay to buy from sellers with no QC badge?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': 'For first purchases, stick to QC-verified listings. Once experienced, cautiously explore unverified listings while always applying the safety checklist.',
                },
              },
            ],
          }),
        }}
      />
    </div>
  );
}